import { action, computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal, Signal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate, KnownColumnTemplateKey } from '@thinkalpha/ui-types/contracts/column-template';
import { isEqual } from 'es-toolkit';
import { modelDisposableStack } from 'src/lib/signals/modelDisposableStack';
import { getColumnTemplateByIdQuery, getDefaultColumnTemplateQuery } from 'src/queries/columnTemplates';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { ReactBindings } from 'src/types/bindings';
import type {
    IsColumnTemplateLibraryOpenSignal,
    OpenColumnTemplateLibraryEvent,
    SelectColumnTemplateEvent,
    SelectedColumnTemplateSignal,
    SetColumnTemplateLibraryOpenEvent,
    UpdateConcreteTemplateEvent,
} from 'src/types/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';
import { z } from 'zod';

const concreteColumnTemplateSchema = z.custom<ColumnTemplate>(
    (value) => typeof value === 'object' && value !== null && Array.isArray((value as { columns?: unknown }).columns),
    { message: 'Expected a concrete column template with a columns array' },
);

/**
 * A persisted column template reference: `null` means "use the user's default
 * template for the key", a string is a saved template id, and a concrete
 * object is a template the user has locally modified.
 */
export const columnTemplateStateSchema = z.object({
    columnTemplate: z.union([z.string(), concreteColumnTemplateSchema, z.null()]).default(null),
});

export type ColumnTemplateState = z.output<typeof columnTemplateStateSchema>;
export type ColumnTemplateRef = ColumnTemplateState['columnTemplate'];

/** How a resolved template came to be applied — lets consumers distinguish reference switches from in-place edits. */
export type ColumnTemplateApplyCause = 'initial' | 'reference-change' | 'concrete-update';

export type ColumnTemplateModelEventBus = EventBus<
    SelectedColumnTemplateSignal & IsColumnTemplateLibraryOpenSignal,
    SelectColumnTemplateEvent &
        UpdateConcreteTemplateEvent &
        SetColumnTemplateLibraryOpenEvent &
        OpenColumnTemplateLibraryEvent
>;

export interface ColumnTemplateModelInit {
    persisted: ColumnTemplateState;
    templateKey: KnownColumnTemplateKey;
    queryClient: ReactBindings['QueryClient'];
    logger: Logger;
    /**
     * Invoked whenever a resolved template is applied. `initial` and
     * `reference-change` mean a different template was loaded (consumers
     * typically re-seed dependent state such as the sort model);
     * `concrete-update` is an in-place edit of the current template.
     */
    onTemplateApplied?: (template: ColumnTemplate | null, cause: ColumnTemplateApplyCause) => void;
}

export interface ColumnTemplateModelShape {
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: ColumnTemplateModelEventBus;
    /** The persisted reference (null/id/concrete) — what toJSON serializes. */
    readonly columnTemplateRef: ReadonlySignal<ColumnTemplateRef>;
    /** The fully resolved, sanitized concrete template, or null while resolving. */
    readonly columnTemplate: ReadonlySignal<ColumnTemplate | null>;
    readonly columnTemplateError: ReadonlySignal<Error | null>;
    updateConcreteTemplate(template: ColumnTemplate): void;
    toJSON(): ColumnTemplateState;
    markClean(): void;
}

function asError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
}

/**
 * Owns a widget's column template: the persisted reference, its resolution to
 * a concrete template, dirty tracking, and the template-related EventBus
 * contract. Resolution completes before the resolved signal flips, so
 * consumers gating work (e.g. table creation) on `columnTemplate` never see a
 * half-resolved template.
 */
export const ColumnTemplateModel: ModelConstructor<ColumnTemplateModelShape, [ColumnTemplateModelInit]> = createModel<
    ColumnTemplateModelShape,
    [ColumnTemplateModelInit]
>((init: ColumnTemplateModelInit) => {
    const persistedRef: Signal<ColumnTemplateRef> = signal(init.persisted.columnTemplate);
    const cleanRef: Signal<ColumnTemplateRef> = signal(init.persisted.columnTemplate);
    const resolved: Signal<ColumnTemplate | null> = signal(null);
    const resolveError: Signal<Error | null> = signal(null);
    const isColumnTemplateLibraryOpen = signal(false);

    const disposableStack = modelDisposableStack();
    let resolveRequestId = 0;

    /**
     * Applies a resolved template and its side effects as one atomic
     * transition. An action (not bare writes) because the resolution
     * continuation runs outside createModel's implicit action wrapping, and
     * consumers gating on "template resolved" (e.g. a sort seeded from the
     * template via onTemplateApplied) must never observe the template without
     * the callback's writes.
     */
    const applyResolvedTemplate = action((sanitized: ColumnTemplate | null, cause: ColumnTemplateApplyCause): void => {
        resolved.value = sanitized;
        init.onTemplateApplied?.(sanitized, cause);
    });

    const resolveTemplate = async (ref: ColumnTemplateRef, cause: ColumnTemplateApplyCause): Promise<void> => {
        const requestId = ++resolveRequestId;
        resolveError.value = null;

        try {
            const template =
                ref === null
                    ? await init.queryClient.fetchQuery(getDefaultColumnTemplateQuery(init.templateKey))
                    : typeof ref === 'string'
                      ? await init.queryClient.fetchQuery(getColumnTemplateByIdQuery(ref))
                      : ref;

            if (disposableStack.disposed || requestId !== resolveRequestId) return;

            const sanitized = template ? generateWorkableColumnTemplateGridSettings(template) : null;
            applyResolvedTemplate(sanitized, cause);
        } catch (error) {
            if (disposableStack.disposed || requestId !== resolveRequestId) return;

            const normalized = asError(error);
            init.logger.error('Failed to resolve column template', {
                error: normalized,
                templateKey: init.templateKey,
                ref: typeof ref === 'string' ? ref : ref === null ? 'default' : 'concrete',
            });
            // Initial resolution failure leaves the widget without a template
            // (fatal for table creation); later failures keep the current one.
            if (cause === 'initial') {
                resolved.value = null;
                resolveError.value = normalized;
            }
        }
    };

    const selectColumnTemplate = (templateId: string): void => {
        if (persistedRef.value === templateId) return;
        persistedRef.value = templateId;
        void resolveTemplate(templateId, 'reference-change');
    };

    const updateConcreteTemplate = action((template: ColumnTemplate): void => {
        // A concrete edit is the latest user intent: advancing the request id cancels any
        // in-flight resolution so a slow fetch cannot land afterward and overwrite the edit.
        resolveRequestId += 1;
        persistedRef.value = template;
        // The raw template is what persists; the resolved signal carries the sanitized form,
        // matching the resolve path (the shape documents `columnTemplate` as sanitized).
        const sanitized = generateWorkableColumnTemplateGridSettings(template);
        resolved.value = sanitized;
        init.onTemplateApplied?.(sanitized, 'concrete-update');
    });

    void resolveTemplate(persistedRef.value, 'initial');

    const setLibraryOpen = ({ isOpen }: { isOpen: boolean }): void => {
        isColumnTemplateLibraryOpen.value = isOpen;
    };

    const eventBus: ColumnTemplateModelEventBus = EventBusBuilder.create()
        .addSignal(
            'widget::selectedColumnTemplate',
            (): ReadonlySignal<string | ColumnTemplate | null> => computed(() => resolved.value),
        )
        .addSignal('widget::isColumnTemplateLibraryOpen', () => computed(() => isColumnTemplateLibraryOpen.value))
        .addEvent('widget::selectColumnTemplate', ({ templateId }) => {
            selectColumnTemplate(templateId);
        })
        .addEvent('widget::updateConcreteTemplate', ({ columnTemplate }) => {
            updateConcreteTemplate(columnTemplate);
        })
        .addEvent('widget::setColumnTemplateLibraryOpen', setLibraryOpen)
        .addEvent('widget::onColumnTemplateLibraryOpen', setLibraryOpen)
        .build();

    return {
        dirty: computed(() => !isEqual(persistedRef.value, cleanRef.value)),
        eventBus,
        columnTemplateRef: computed(() => persistedRef.value),
        columnTemplate: computed(() => resolved.value),
        columnTemplateError: computed(() => resolveError.value),
        updateConcreteTemplate,
        toJSON(): ColumnTemplateState {
            return { columnTemplate: persistedRef.value };
        },
        markClean(): void {
            cleanRef.value = persistedRef.value;
        },
    };
});

export type ColumnTemplateModel = Model<ColumnTemplateModelShape>;
